import { isEqual } from 'lodash'
import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '#app/store'
import { type MarketEntry, selectMarketByConfiguration } from '#app/store/slices/configuration/marketSlice'
import type { ConfigurationOptionalEquipment, LocalConfigurationId, TruckConfiguration } from '#app/store/slices/configurationSlice'
import { type ConfigurationEnrichmentData, enrichConfiguration } from '#app/store/slices/enrichConfiguration'
import type { ConfigurationMode } from '#features/Configuration/ApiConfiguration'
import type { FavoritesState } from '#store/slices/userData/favoritesSlice'
import { log } from '#utils/logger'
import { useActiveConfiguration } from '../components/useActiveConfiguration'

const globalEnrichmentCache = new Map<
  LocalConfigurationId,
  {
    lastProductGroups: TruckConfiguration['productGroups']
    lastInitialSelection: TruckConfiguration['initialSelection']
    lastConfiguredFamilies: TruckConfiguration['configuredFamilies']
    lastMarketData: MarketEntry
    lastFavorites: FavoritesState
    enrichment: ConfigurationEnrichmentData
    mode?: ConfigurationMode
    step: TruckConfiguration['step']['system']
    language?: string
    optionalEquipments: ConfigurationOptionalEquipment[]
    compatibility: TruckConfiguration['compatibility']
  }
>()

export const useConfigurationEnrichment = (configuration: TruckConfiguration | undefined) => {
  const favorites = useAppSelector((state) => state.userData.favorites)
  const marketData = useAppSelector((state) => selectMarketByConfiguration(state, configuration))

  const configurations = useAppSelector((state) => state.truckConfigurations.configurations)
  const lastConfigurationIdsRef = useRef<string[]>([])
  useEffect(() => {
    const ids = Object.keys(configurations)

    // configurations changes often, ids only rarely
    if (isEqual(ids, lastConfigurationIdsRef.current)) {
      return
    }

    for (const cachedConfigurationId of globalEnrichmentCache.keys()) {
      if (!ids.includes(cachedConfigurationId)) {
        log.log('Removing enrichment for', cachedConfigurationId)
        globalEnrichmentCache.delete(cachedConfigurationId)
      }
    }

    lastConfigurationIdsRef.current = ids
  }, [configurations])

  const {
    i18n: { language },
  } = useTranslation()

  const enrichment = useMemo(() => {
    const rawConfiguration = configuration
    if (!rawConfiguration) {
      return undefined
    }

    const cached = globalEnrichmentCache.get(rawConfiguration.id)
    if (
      cached &&
      isEqual(cached.lastProductGroups, rawConfiguration.productGroups) &&
      isEqual(cached.lastInitialSelection, rawConfiguration.initialSelection) &&
      isEqual(cached.lastConfiguredFamilies, rawConfiguration.configuredFamilies) &&
      isEqual(cached.lastMarketData, marketData) &&
      isEqual(cached.lastFavorites, favorites) &&
      isEqual(cached.mode, rawConfiguration.apiConfiguration.mode) &&
      isEqual(cached.step, rawConfiguration.step.system) &&
      isEqual(cached.language, language) &&
      isEqual(cached.optionalEquipments, rawConfiguration.optionalEquipment) &&
      isEqual(cached.compatibility, rawConfiguration.compatibility)
    ) {
      return cached.enrichment
    }
    const enrichment = enrichConfiguration(
      rawConfiguration.productGroups,
      rawConfiguration.configuredFamilies,
      rawConfiguration.initialSelection,
      marketData,
      favorites,
      rawConfiguration.apiConfiguration.mode,
      rawConfiguration.step.system,
      rawConfiguration.apiConfiguration,
      rawConfiguration.optionalEquipment,
      rawConfiguration.tg3?.tg3ConversionInsightsLookup,
      { currentConfigurationVersion: rawConfiguration.version, compatibility: rawConfiguration.compatibility },
    )
    globalEnrichmentCache.set(rawConfiguration.id, {
      lastProductGroups: rawConfiguration.productGroups,
      lastInitialSelection: rawConfiguration.initialSelection,
      lastConfiguredFamilies: rawConfiguration.configuredFamilies,
      lastMarketData: marketData,
      lastFavorites: favorites,
      enrichment: enrichment,
      mode: rawConfiguration.apiConfiguration.mode,
      step: rawConfiguration.step.system ?? 'unknown',
      language,
      optionalEquipments: rawConfiguration.optionalEquipment,
      compatibility: rawConfiguration.compatibility,
    })
    return enrichment
  }, [configuration, favorites, language, marketData])

  return {
    configuration,
    enrichment,
  }
}

export const useActiveConfigurationEnrichment = () => {
  const configuration = useActiveConfiguration()
  return useConfigurationEnrichment(configuration)
}
